'use client';

import { useEffect, useRef, useState } from 'react';

import breakpoints from '@/lib/breakpoints';
import { cn } from '@/lib/utils';
import { useMediaQuery } from '@/hooks/useMediaQuery';

import { SplitedTextProps } from '@/models/Headings';

export default function SplitedText(props: SplitedTextProps) {
  const {
    text,
    anchor,
    init = false,
    fontWeight = '600',
    offset,
    stroke = false,
    align = 'left',
  } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const [animatedText, setAnimatedText] = useState<string>('');
  const isDesktop = useMediaQuery(`(min-width: ${breakpoints.sm})`);
  const [splitWord, setSplitWord] = useState<string | RegExp>();
  const [weight, setWeight] = useState<string>('font-semibold');

  useEffect(() => {
    if (isDesktop) {
      setSplitWord('<br />');
    } else {
      setSplitWord(' ');
    }
  }, [isDesktop]);

  useEffect(() => {
    if (fontWeight) {
      switch (fontWeight) {
        case '400':
          setWeight('font-normal');
          break;
        case '500':
          setWeight('font-medium');
          break;
        case '600':
          setWeight('font-semibold');
          break;
        case '700':
          setWeight('font-bold');
          break;
      }
    }
  }, [fontWeight]);

  useEffect(() => {
    if (!splitWord) return;

    const splitText = (text: string) => {
      const delay = 0.4;
      let lines;

      lines = splitWord === ' ' ? text.replace('<br />', ' ') : text;
      lines = lines.split(splitWord);

      const generateLetters = (line: string) =>
        line
          .split('')
          .map(
            (letter, index) =>
              `<span class="letter${
                letter === ' ' ? ' space' : ''
              }" style="transition-delay: ${
                Math.round((delay + 0.04 * index) * 100) / 100
              }s">${letter}</span>`,
          )
          .join('');

      const spans = lines.map(
        (line) => `<span class="word">${generateLetters(line)}</span>`,
      );

      return spans.join('');
    };

    const title = splitText(text);
    setAnimatedText(title);

    if (init) {
      setTimeout(
        () => ref?.current?.classList.add('animated-heading--init'),
        100,
      );
    }
  }, [text, splitWord, init]);

  return (
    <>
      {animatedText ? (
        <span
          aria-hidden='true'
          dangerouslySetInnerHTML={{ __html: animatedText }}
          className={cn(
            'animated-heading',
            init ? 'init' : '',
            stroke && 'text-stroke',
            weight,
            [
              align === 'left' && 'flex sm:block',
              align === 'right' && 'flex items-end md:flex-col',
              align === 'center' &&
                'flex items-center justify-center md:flex-col',
            ],
          )}
          data-aos='fade-in'
          data-aos-anchor={anchor || null}
          data-aos-offset={offset || '0'}
          ref={ref}
        />
      ) : (
        <span
          aria-hidden='true'
          dangerouslySetInnerHTML={{ __html: text }}
          className={cn(
            'leading-[1.3]',
            'pointer-events-none text-transparent opacity-0',
            weight,
            [
              align === 'left' && 'flex sm:block',
              align === 'right' && 'flex items-end md:flex-col',
              align === 'center' &&
                'flex items-center justify-center md:flex-col',
            ],
          )}
          data-aos='fade-in'
          data-aos-anchor={anchor || null}
          ref={ref}
        />
      )}
    </>
  );
}
